import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import api from "../utils/axios";

// Load Stripe with your publishable key from .env file
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_your_key_here"
);

// Define context types
type StripeContextType = {
  subscribeBasic: () => Promise<string>;
  subscribePro: () => Promise<string>;
  cancelSubscription: () => Promise<void>;
  loading: boolean;
  error: string | null;
};

// Create context
const StripeContext = createContext<StripeContextType | null>(null);

// Custom hook to use the Stripe context
export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error("useStripe must be used within a StripeProvider");
  }
  return context;
};

// Provider component
export const StripeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize Stripe
    const initStripe = async () => {
      try {
        // Just to ensure Stripe is loaded
        const stripe = await stripePromise;
        if (!stripe) {
          setError("No se pudo cargar Stripe. Verifica tu clave de API.");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error initializing Stripe:", error);
        setError(
          "Error al inicializar Stripe. Por favor, inténtalo más tarde."
        );
        setLoading(false);
      }
    };

    initStripe();
  }, []);

  // Create a checkout session for the basic plan
  const subscribeBasic = async (): Promise<string> => {
    try {
      setError(null);
      console.log("Creando sesión de checkout para Plan Basic...");
      const response = await api.post("/stripe/create-checkout-session/basic");

      if (!response.data || !response.data.checkout_url) {
        throw new Error(
          "No se recibió una URL de checkout válida del servidor"
        );
      }

      const checkoutUrl = response.data.checkout_url;
      console.log(`URL de checkout recibida: ${checkoutUrl}`);

      // Realizar la redirección
      window.location.assign(checkoutUrl);

      return checkoutUrl;
    } catch (error: any) {
      console.error("Error creating checkout session for Basic plan:", error);

      // Extraer mensaje detallado del error si existe
      let errorMessage = "Error al crear la sesión de checkout.";
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Create a checkout session for the pro plan
  const subscribePro = async (): Promise<string> => {
    try {
      setError(null);
      console.log("Creando sesión de checkout para Plan Pro...");
      const response = await api.post("/stripe/create-checkout-session/pro");

      if (!response.data || !response.data.checkout_url) {
        throw new Error(
          "No se recibió una URL de checkout válida del servidor"
        );
      }

      const checkoutUrl = response.data.checkout_url;
      console.log(`URL de checkout recibida: ${checkoutUrl}`);

      // Realizar la redirección
      window.location.assign(checkoutUrl);

      return checkoutUrl;
    } catch (error: any) {
      console.error("Error creating checkout session for Pro plan:", error);

      // Extraer mensaje detallado del error si existe
      let errorMessage = "Error al crear la sesión de checkout.";
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Cancel subscription
  const cancelSubscription = async (): Promise<void> => {
    try {
      setError(null);
      await api.post("/stripe/cancel-subscription");
    } catch (error: any) {
      console.error("Error cancelling subscription:", error);

      // Extraer mensaje detallado del error si existe
      let errorMessage = "Error al cancelar la suscripción.";
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Provide the context
  return (
    <StripeContext.Provider
      value={{
        subscribeBasic,
        subscribePro,
        cancelSubscription,
        loading,
        error,
      }}
    >
      <Elements stripe={stripePromise}>{children}</Elements>
    </StripeContext.Provider>
  );
};

export default StripeContext;
